import { Switch, withStyles } from "@material-ui/core";
import _ from "lodash";
import { findIndex } from "ramda";
import * as React from "react";
import LazyLoad from "react-lazy-load";
import { connect } from "react-redux";
import { DEFAULT_LANGUAGE } from "src/constants";
import { getMappingTogglesEnabled } from "src/metadata/selector";
import { State } from "../../state";
import { getTagMode } from "../chartOfAccounts/Tag/selector";
import TagHighlight from "../chartOfAccounts/Tag/TagHighlight";
import { SpreadPagePresenter } from "../model/presenter/pagePresenters";
import { getPagePresenters } from "../model/presenter/selector";
import { getIsAccountSelected } from "../model/selectedAccounts/selector";
import { getMappingNodeId, getSpread } from "../model/spread/selector";
import { Spread } from "../model/spread/types";
import "./RawSpread.scss";
import SpreadImage from "./SpreadImage";
import SpreadRowHighlight from "./SpreadRowHighlight";

export const MappedSwitch = withStyles({
  switchBase: {
    color: "#F2F2F2",
    "&$checked": {
      color: "#009BDF"
    },
    "&$checked + $bar": {
      backgroundColor: "#009BDF",
      opacity: 0.5
    }
  },
  checked: {},
  bar: {},
  root: { marginRight: "18px" }
})(Switch);

export const UnmappedSwitch = withStyles({
  switchBase: {
    color: "#F2F2F2",
    "&$checked": {
      color: "#F8B405"
    },
    "&$checked + $bar": {
      backgroundColor: "#F8B405",
      opacity: 0.5
    }
  },
  checked: {},
  bar: {}
})(Switch);

export const TranslationSwitch = withStyles({
  switchBase: {
    color: "#F2F2F2",
    "&$checked": {
      color: "#ff9a00"
    },
    "&$checked + $bar": {
      backgroundColor: "#ff9a00",
      opacity: 0.5
    }
  },
  checked: {},
  bar: {}
})(Switch);

export const MagnifierSwitch = withStyles({
  switchBase: {
    color: "#F2F2F2",
    "&$checked": {
      color: "#12a32b"
    },
    "&$checked + $bar": {
      backgroundColor: "#12a32b",
      opacity: 0.5
    }
  },
  checked: {},
  bar: {}
})(Switch);

interface SpreadPageProps {
  pagePresenter: SpreadPagePresenter;
  isFirstPageWithCells: boolean;
  getScrollContext: () => Element | null;
  mappedValuesToggledOn: boolean;
  unmappedValuesToggledOn: boolean;
  isAccountSelectedFn: (accountId: string) => boolean;
  mappingNodeId: string | null;
  environmentName?: string;
  activeTab: number;
  englishTransationOn: boolean;
  needsTranslation?: boolean;
  tagMode: boolean;
  maginficationOn: boolean;
  hidden?: boolean;
}

const SpreadPage: React.SFC<SpreadPageProps> = ({
  pagePresenter,
  isFirstPageWithCells,
  getScrollContext,
  mappedValuesToggledOn,
  unmappedValuesToggledOn,
  isAccountSelectedFn,
  mappingNodeId,
  environmentName,
  activeTab,
  englishTransationOn,
  needsTranslation,
  tagMode,
  maginficationOn,
  hidden
}) => (
  <div className="SpreadPage">
    <LazyLoad>
      <React.Fragment>
        <SpreadImage
          groundPage={pagePresenter.spreadGroundPage}
          englishTranslationOn={englishTransationOn}
          magnificationOn={maginficationOn}
          useTranslatedImage={false}
          hidden={hidden}
        />

        {needsTranslation && (
          <SpreadImage
            groundPage={pagePresenter.spreadGroundPage}
            englishTranslationOn={englishTransationOn}
            magnificationOn={maginficationOn}
            useTranslatedImage={true}
            hidden={hidden}
          />
        )}

        {tagMode && (
          <TagHighlight
            pageNumber={pagePresenter.spreadGroundPage.pageNumber}
          />
        )}
      </React.Fragment>
    </LazyLoad>

    {!englishTransationOn &&
      !maginficationOn &&
      (unmappedValuesToggledOn || mappingNodeId !== null) &&
      pagePresenter.unmappedAccounts.map(
        (unmappedAccount, index) =>
          unmappedAccount.accountRow && (
            <SpreadRowHighlight
              accountRow={unmappedAccount.accountRow}
              getScrollContext={getScrollContext}
              key={`${unmappedAccount.valueCell.id} - ${index}`}
              environmentName={environmentName}
              mappingNodeId={mappingNodeId}
              adjustedOcrText={unmappedAccount.labelCell.adjustedOcrText}
              groundPage={pagePresenter.spreadGroundPage}
            />
          )
      )}

    {!englishTransationOn &&
      !maginficationOn &&
      pagePresenter.mappedAccounts.map((mappedAccountRow, index) => {
        const isAccountSelected = isAccountSelectedFn(
          mappedAccountRow.accountId
        );
        return (
          mappedAccountRow &&
          mappedAccountRow.statementType === activeTab &&
          (mappedValuesToggledOn ||
            mappingNodeId !== null ||
            isAccountSelected) && (
            <SpreadRowHighlight
              accountRow={mappedAccountRow}
              getScrollContext={getScrollContext}
              isFirstCell={isFirstPageWithCells && index === 0}
              key={`${mappedAccountRow.accountId} - ${index}`}
              isAccountSelected={isAccountSelected}
              mappingNodeId={mappingNodeId}
              environmentName={environmentName}
              groundPage={pagePresenter.spreadGroundPage}
            />
          )
        );
      })}
  </div>
);
interface RawSpreadProps {
  spreadPagePresenters: SpreadPagePresenter[];
  isAccountSelectedFn: (accountId: string) => boolean;
  mappingNodeId: string | null;
  environmentName?: string;
  activeTab: number;
  spread?: Spread;
  mappingTogglesEnabled: boolean;
  tagMode: boolean;
  hidden?: boolean;
}

interface RawSpreadState {
  mappedValuesToggledOn: boolean;
  unmappedValuesToggledOn: boolean;
  englishTransationOn: boolean;
  magnificationOn: boolean;
}

class RawSpread extends React.Component<RawSpreadProps, RawSpreadState> {
  private domNode: Element | null = null;

  constructor(props: RawSpreadProps) {
    super(props);
    this.state = {
      mappedValuesToggledOn: props.mappingTogglesEnabled === true && true,
      unmappedValuesToggledOn: props.mappingTogglesEnabled === true && true,
      englishTransationOn: false,
      magnificationOn: false
    };
  }

  public render() {
    const {
      spreadPagePresenters,
      isAccountSelectedFn,
      mappingNodeId,
      environmentName
    } = this.props;

    const firstPageWithCells = findIndex(
      pagePresenter => pagePresenter.mappedAccounts.length > 0,
      spreadPagePresenters
    );

    return (
      <div className="RawSpread__wrapper">
        <div className="RawSpread__header">
          {this.props.mappingTogglesEnabled && (
            <React.Fragment>
              <div>Mapped Values</div>
              <MappedSwitch
                checked={
                  this.state.mappedValuesToggledOn || mappingNodeId !== null
                }
                onChange={this.handleMappedToggleChange}
                value="checkedMapped"
              />
            </React.Fragment>
          )}
          {this.props.mappingTogglesEnabled && (
            <React.Fragment>
              <div>Unmapped Values</div>
              <UnmappedSwitch
                checked={
                  this.state.unmappedValuesToggledOn || mappingNodeId !== null
                }
                onChange={this.handleUnmappedToggleChange}
                value="checkedUnmapped"
              />
            </React.Fragment>
          )}
          {this.needsTranslation() && [
            <div key="translation">English Translation</div>,

            <TranslationSwitch
              checked={this.state.englishTransationOn || mappingNodeId !== null}
              onChange={this.handleTranslationToggleChange}
              value="checkedUnmapped"
              key="switch"
            />
          ]}
          {[
            <div key="magnification">Magnify</div>,
            <MagnifierSwitch
              checked={this.state.magnificationOn}
              onChange={this.handleMagnificationToggleChange}
              value="checkedUnmapped"
              key="switch"
            />
          ]}
        </div>
        <div
          ref={node => (this.domNode = node)}
          className="RawSpread__container scrolling"
        >
          {spreadPagePresenters.map((pagePresenter, index) => (
            <SpreadPage
              tagMode={this.props.tagMode}
              pagePresenter={pagePresenter}
              key={pagePresenter.spreadGroundPage.pageNumber}
              isFirstPageWithCells={index === firstPageWithCells}
              getScrollContext={this.getScrollContext}
              mappedValuesToggledOn={this.state.mappedValuesToggledOn}
              unmappedValuesToggledOn={this.state.unmappedValuesToggledOn}
              isAccountSelectedFn={isAccountSelectedFn}
              mappingNodeId={mappingNodeId}
              environmentName={environmentName}
              activeTab={this.props.activeTab}
              englishTransationOn={this.state.englishTransationOn}
              needsTranslation={this.needsTranslation()}
              maginficationOn={this.state.magnificationOn}
              hidden={this.props.hidden}
            />
          ))}
        </div>
      </div>
    );
  }

  private needsTranslation = () => {
    return (
      this.props.spread &&
      _.lowerCase(this.props.spread.language) !== DEFAULT_LANGUAGE
    );
  };

  private handleMappedToggleChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!this.props.mappingNodeId) {
      this.setState({ mappedValuesToggledOn: event.target.checked });
    }
  };

  private handleUnmappedToggleChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!this.props.mappingNodeId) {
      this.setState({ unmappedValuesToggledOn: event.target.checked });
    }
  };

  private handleTranslationToggleChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!this.props.mappingNodeId) {
      this.setState({ englishTransationOn: event.target.checked });
    }
  };

  private handleMagnificationToggleChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    this.setState({ magnificationOn: event.target.checked });
  };

  private getScrollContext = () => {
    return this.domNode;
  };
}

// TODO: Use standard export when https://github.com/Microsoft/TypeScript/issues/14127 is resolved
export { RawSpread, SpreadPage };

const mapStateToProps = (state: State) => ({
  spreadPagePresenters: getPagePresenters(state),
  isAccountSelectedFn: getIsAccountSelected(state),
  mappingNodeId: getMappingNodeId(state),
  spread: getSpread(state),
  mappingTogglesEnabled: getMappingTogglesEnabled(state),
  tagMode: getTagMode(state)
});
export default connect(mapStateToProps)(RawSpread);



// WEBPACK FOOTER //
// ./src/spread/rawSpread/RawSpread.tsx